import sodium from 'libsodium-wrappers-sumo';

export const MAGIC = new Uint8Array([0x50, 0x45, 0x53, 0x31]); // "PES1"
export const VERSION = 1;
export const KDF_ARGON2ID13 = 1;
export const HEADER_LEN = 80;
export const TAG_LEN = 16;
export const DEFAULT_CHUNK = 8 * 1024 * 1024;
export const DEFAULT_KDF = Object.freeze({ id: KDF_ARGON2ID13, opsLimit: 3, memLimit: 65536 });

export const CTX = Object.freeze({
  auth: 'pes-auth',
  file: 'pes-file',
  name: 'pes-name',
  manifest: 'pes-mnf0',
});

const SUBKEY_LEN = 32;

let readyPromise;
export function readySodium() {
  if (!readyPromise) readyPromise = sodium.ready;
  return readyPromise;
}

export function normalizePath(input) {
  const parts = String(input).normalize('NFC').split('/').filter((s) => s.length > 0);
  if (parts.some((s) => s === '.' || s === '..')) throw new Error('path must not contain "." or ".." segments');
  return '/' + parts.join('/');
}

export function deriveMasterKey(password, salt, kdf = DEFAULT_KDF) {
  if (salt.length !== 16) throw new Error('salt must be 16 bytes');
  // libsodium / libsodium.js take memlimit in BYTES while the container stores KiB.
  return sodium.crypto_pwhash(
    SUBKEY_LEN,
    String(password).normalize('NFC'),
    salt,
    kdf.opsLimit,
    kdf.memLimit * 1024,
    sodium.crypto_pwhash_ALG_ARGON2ID13,
  );
}

export function deriveSubkey(masterKey, id, ctx) {
  if (masterKey.length !== SUBKEY_LEN) throw new Error('masterKey must be 32 bytes');
  if (ctx.length !== 8) throw new Error('ctx must be 8 bytes');
  return sodium.crypto_kdf_derive_from_key(SUBKEY_LEN, id, ctx, masterKey);
}

export function deriveKeys(masterKey) {
  return {
    authKey: deriveSubkey(masterKey, 1, CTX.auth),
    fileKey: deriveSubkey(masterKey, 2, CTX.file),
    nameKey: deriveSubkey(masterKey, 3, CTX.name),
    manifestKey: deriveSubkey(masterKey, 4, CTX.manifest),
  };
}

function keyBytes(nameKey) {
  if (typeof nameKey === 'string') return new TextEncoder().encode(nameKey.normalize('NFC'));
  return nameKey instanceof Uint8Array ? nameKey : new Uint8Array(nameKey);
}

export async function pathToObjectId(nameKey, path) {
  const key = await crypto.subtle.importKey('raw', keyBytes(nameKey), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  const sig = await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(normalizePath(path)));
  return toHex(new Uint8Array(sig));
}

export function objectKeyFor(userId, objectId) {
  if (!/^[a-f0-9]{32}$/.test(userId)) throw new Error('bad userId');
  if (!/^[a-f0-9]{64}$/.test(objectId)) throw new Error('bad objectId');
  return `u/${userId}/${objectId}`;
}

export function manifestObjectKey(userId) {
  if (!/^[a-f0-9]{32}$/.test(userId)) throw new Error('bad userId');
  return `u/${userId}/_manifest`;
}

function aadFor(fileId, chunkIndex, isLast) {
  const aad = new Uint8Array(26);
  aad.set(MAGIC, 0);
  aad[4] = VERSION;
  aad.set(fileId, 5);
  new DataView(aad.buffer).setUint32(21, chunkIndex, false);
  aad[25] = isLast ? 1 : 0;
  return aad;
}

function chunkNonce(fileNonce, chunkIndex) {
  const nonce = new Uint8Array(24);
  nonce.set(fileNonce, 0);
  new DataView(nonce.buffer).setBigUint64(16, BigInt(chunkIndex), false);
  return nonce;
}

export function chunkCount(plainSize, chunkPlainSize) {
  return Math.max(1, Math.ceil(plainSize / chunkPlainSize));
}

function sealChunk(plain, fileKey, fileNonce, fileId, index, isLast) {
  return sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
    plain,
    aadFor(fileId, index, isLast),
    null,
    chunkNonce(fileNonce, index),
    fileKey,
  );
}

function openChunk(cipher, fileKey, fileNonce, fileId, index, isLast) {
  return sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
    null,
    cipher,
    aadFor(fileId, index, isLast),
    chunkNonce(fileNonce, index),
    fileKey,
  );
}

export function buildHeader({ salt, kdf, chunkPlainSize, fileNonce, fileId }) {
  const header = new Uint8Array(HEADER_LEN);
  header.set(MAGIC, 0);
  header[4] = VERSION;
  header[5] = kdf.id;
  const dv = new DataView(header.buffer);
  dv.setUint32(6, kdf.opsLimit, true);
  dv.setUint32(10, kdf.memLimit, true);
  dv.setUint32(14, chunkPlainSize, true);
  header.set(salt, 18);
  header.set(fileNonce, 34);
  header.set(fileId, 50);
  return header;
}

export function parseHeader(bytes) {
  if (bytes.length < HEADER_LEN) throw new Error('truncated header');
  for (let i = 0; i < 4; i++) if (bytes[i] !== MAGIC[i]) throw new Error('bad magic');
  if (bytes[4] !== VERSION) throw new Error(`unsupported version ${bytes[4]}`);
  if (bytes[5] !== KDF_ARGON2ID13) throw new Error(`unsupported kdf ${bytes[5]}`);
  const dv = new DataView(bytes.buffer, bytes.byteOffset, HEADER_LEN);
  return {
    kdf: { id: bytes[5], opsLimit: dv.getUint32(6, true), memLimit: dv.getUint32(10, true) },
    chunkPlainSize: dv.getUint32(14, true),
    salt: bytes.slice(18, 34),
    fileNonce: bytes.slice(34, 50),
    fileId: bytes.slice(50, 66),
  };
}

export function newContainerIds() {
  return { fileNonce: sodium.randombytes_buf(16), fileId: sodium.randombytes_buf(16) };
}

export function encryptBytes(plain, fileKey, opts) {
  const { salt, kdf = DEFAULT_KDF, chunkPlainSize = DEFAULT_CHUNK } = opts;
  const { fileNonce, fileId } = opts.fileNonce && opts.fileId
    ? { fileNonce: opts.fileNonce, fileId: opts.fileId }
    : newContainerIds();
  const n = chunkCount(plain.length, chunkPlainSize);
  const parts = [buildHeader({ salt, kdf, chunkPlainSize, fileNonce, fileId })];
  for (let i = 0; i < n; i++) {
    const start = i * chunkPlainSize;
    const slice = plain.subarray(start, Math.min(start + chunkPlainSize, plain.length));
    parts.push(sealChunk(slice, fileKey, fileNonce, fileId, i, i === n - 1));
  }
  return { bytes: concat(parts), fileId, fileNonce, header: parts[0] };
}

export function decryptBytes(container, fileKey) {
  const header = parseHeader(container);
  const body = container.subarray(HEADER_LEN);
  const step = header.chunkPlainSize + TAG_LEN;
  const n = Math.max(1, Math.ceil(body.length / step));
  const out = [];
  let offset = 0;
  for (let i = 0; i < n; i++) {
    const len = i === n - 1 ? body.length - offset : step;
    if (len < TAG_LEN) throw new Error('truncated chunk');
    out.push(openChunk(body.subarray(offset, offset + len), fileKey, header.fileNonce, header.fileId, i, i === n - 1));
    offset += len;
  }
  return { plain: concat(out), header };
}

/** Encrypts a Blob/File lazily so only one chunk is resident in memory. */
export async function* encryptBlob(blob, fileKey, opts) {
  const { salt, kdf = DEFAULT_KDF, chunkPlainSize = DEFAULT_CHUNK } = opts;
  const { fileNonce, fileId } = opts.fileNonce && opts.fileId
    ? { fileNonce: opts.fileNonce, fileId: opts.fileId }
    : newContainerIds();
  const n = chunkCount(blob.size, chunkPlainSize);
  yield buildHeader({ salt, kdf, chunkPlainSize, fileNonce, fileId });
  for (let i = 0; i < n; i++) {
    const start = i * chunkPlainSize;
    const buf = await blob.slice(start, Math.min(start + chunkPlainSize, blob.size)).arrayBuffer();
    yield sealChunk(new Uint8Array(buf), fileKey, fileNonce, fileId, i, i === n - 1);
  }
}

/** Decrypts a streaming response chunk by chunk. */
export async function* decryptStream(readable, fileKey) {
  if (!readable) throw new Error('响应没有可读取的内容');
  const reader = readable.getReader();
  let header = null;
  let buf = new Uint8Array(0);
  let index = 0;

  for (;;) {
    const { done, value } = await reader.read();
    if (value) buf = concat([buf, value]);
    if (!header && buf.length >= HEADER_LEN) {
      header = parseHeader(buf);
      buf = buf.subarray(HEADER_LEN);
    }
    if (!header) {
      if (done) throw new Error('truncated header');
      continue;
    }
    const step = header.chunkPlainSize + TAG_LEN;
    while (buf.length > step) {
      const cipher = buf.subarray(0, step);
      buf = buf.subarray(step);
      yield openChunk(cipher, fileKey, header.fileNonce, header.fileId, index++, false);
    }
    if (done) {
      if (buf.length < TAG_LEN) throw new Error('truncated file');
      yield openChunk(buf, fileKey, header.fileNonce, header.fileId, index, true);
      return;
    }
  }
}

export function toHex(bytes) {
  let s = '';
  for (const b of bytes) s += b.toString(16).padStart(2, '0');
  return s;
}

export function fromHex(hex) {
  if (hex.length % 2) throw new Error('odd hex length');
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.substr(i * 2, 2), 16);
  return out;
}

export function concat(list) {
  const total = list.reduce((n, a) => n + a.length, 0);
  const out = new Uint8Array(total);
  let o = 0;
  for (const a of list) {
    out.set(a, o);
    o += a.length;
  }
  return out;
}

export async function sha256Hex(bytes) {
  return toHex(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)));
}

export { sodium };
